import React, { useEffect } from 'react';
import {
  Row,
  Col,
  Card,
  Typography,
  Alert,
  Spin,
  Tag,
  Table,
  Statistic,
  Button,
} from 'antd';
import {
  WarningOutlined,
  InboxOutlined,
  RiseOutlined,
  BankOutlined,
  EyeOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { fetchAnalytics } from '../store/slices/analyticsSlice';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { formatForTable } from '../utils/timezone';

const Dashboard: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { data: stockData, loading, error } = useAppSelector((state) => state.analytics);

  useEffect(() => {
    dispatch(fetchAnalytics());
  }, [dispatch]);

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (error) {
    return (
      <Alert
        message="Error"
        description={error}
        type="error"
        showIcon
        style={{ marginBottom: 16 }}
      />
    );
  }

  if (!stockData) {
    return (
      <Alert
        message="No Data"
        description="No data available"
        type="info"
        showIcon
      />
    );
  }

  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8'];

  const lowStockColumns = [
    { title: 'Branch', dataIndex: 'branch_name', key: 'branch_name' },
    { title: 'Medicine', dataIndex: 'medicine_name', key: 'medicine_name' },
    { title: 'Category', dataIndex: 'category_name', key: 'category_name' },
    { title: 'Available', dataIndex: 'current_stock', key: 'current_stock' },
    { title: 'Min Level', dataIndex: 'min_stock_level', key: 'min_stock_level' },
    {
      title: 'Shortage',
      key: 'shortage',
      render: (record: any) => record.min_stock_level - record.current_stock,
    },
    {
      title: 'Status',
      key: 'status',
      render: () => <Tag color="orange">LOW STOCK</Tag>,
    },
  ];

  const expiringColumns = [
    { title: 'Branch', dataIndex: 'branch_name', key: 'branch_name' },
    { title: 'Medicine', dataIndex: 'medicine_name', key: 'medicine_name' },
    { title: 'Category', dataIndex: 'category_name', key: 'category_name' },
    {
      title: 'Expiry Date',
      dataIndex: 'expiry_date',
      key: 'expiry_date',
      render: (date: string) => formatForTable(date),
    },
    {
      title: 'Days to Expiry',
      dataIndex: 'days_until_expiry',
      key: 'days_until_expiry',
      render: (days: number) => (
        <Tag color={days <= 7 ? 'red' : 'orange'}>
          {days} days
        </Tag>
      ),
    },
    { title: 'Available Quantity', dataIndex: 'quantity', key: 'quantity' },
  ];

  return (
    <div>
      <Typography.Title level={2} style={{ marginBottom: 24 }}>
        Stock Analytics Dashboard
      </Typography.Title>

      <Row gutter={[16, 16]}>
        {/* Summary Cards */}
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Low Stock Alerts"
              value={stockData.lowStockAlerts?.length || 0}
              prefix={<WarningOutlined style={{ color: '#faad14' }} />}
              valueStyle={{ color: '#faad14' }}
            />
          </Card>
        </Col>

        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Expiring Soon"
              value={stockData.expiringMedicines?.length || 0}
              prefix={<InboxOutlined style={{ color: '#ff4d4f' }} />}
              valueStyle={{ color: '#ff4d4f' }}
            />
          </Card>
        </Col>

        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Total Branches"
              value={stockData.stockSummaryByBranch?.length || 0}
              prefix={<BankOutlined style={{ color: '#1890ff' }} />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>

        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Total Medicines"
              value={stockData.stockSummaryByMedicine?.length || 0}
              prefix={<RiseOutlined style={{ color: '#52c41a' }} />}
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>

        {/* Inventory Value by Branch Chart */}
        <Col xs={24} md={16}>
          <Card title="Inventory Value by Branch">
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={stockData.stockSummaryByBranch || []}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="branch_name" />
                <YAxis />
                <Tooltip formatter={(value) => [`₹${value}`, 'Inventory Value']} />
                <Bar dataKey="total_inventory_value" fill="#1890ff" />
              </BarChart>
            </ResponsiveContainer>
          </Card>
        </Col>

        {/* Stock Status Distribution */}
        <Col xs={24} md={8}>
          <Card title="Stock Status Distribution">
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={[
                    { 
                      name: 'Normal', 
                      value: stockData.stockSummaryByBranch?.reduce((acc: number, branch: any) => acc + (branch.total_items - branch.low_stock_items), 0) || 0 
                    },
                    { 
                      name: 'Low Stock', 
                      value: stockData.stockSummaryByBranch?.reduce((acc: number, branch: any) => acc + branch.low_stock_items, 0) || 0 
                    },
                  ]}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }: any) => `${name} ${(percent * 100).toFixed(0)}%`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {[0, 1].map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </Card>
        </Col>

        {/* Low Stock Alerts Table */}
        <Col xs={24}>
          <Card 
            title="Low Stock Alerts"
            extra={
              <Button 
                type="link" 
                icon={<EyeOutlined />}
                onClick={() => navigate('/main/inventory-requests')}
              >
                View All
              </Button>
            }
          >
            <Table
              dataSource={Array.isArray(stockData.lowStockAlerts) ? stockData.lowStockAlerts.slice(0, 10) : []}
              columns={lowStockColumns}
              rowKey={(record: any, index?: number) => index?.toString() || '0'}
              pagination={false}
              size="small"
              scroll={{ x: 600 }}
            />
          </Card>
        </Col>

        {/* Expiring Medicines Table */}
        <Col xs={24}>
          <Card title="Expiring Medicines (Next 30 Days)">
            <Table
              dataSource={Array.isArray(stockData.expiringMedicines) ? stockData.expiringMedicines.slice(0, 10) : []}
              columns={expiringColumns}
              rowKey={(record: any, index?: number) => index?.toString() || '0'}
              pagination={false}
              size="small"
              scroll={{ x: 600 }}
            />
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default Dashboard;
